<template>
  <div>
    <div class="section-head d-flex justify-content-start align-items-center mb-2">
      <span class="section-title">
        {{ $l('table_label_inspection_report_section') || '验货报告' }}
      </span>
      <el-divider direction="vertical" class="mx-2" />
      <b-button
        class="mr-2"
        size="sm"
        variant="outline-success"
        :disabled="disabled || !inspectionApplyCode"
        @click="triggerReportUpload"
      >
        {{ $l('btn_upload_inspection_report') || '上传验货报告' }}
      </b-button>
    </div>

    <el-upload
      ref="reportUploader"
      class="d-none"
      action="#"
      multiple
      :show-file-list="false"
      :auto-upload="false"
      :on-change="onReportFilesChangeDebounced"
    >
      <el-button ref="reportUploadBtn" slot="trigger" class="d-none" />
    </el-upload>

    <vxe-grid
      size="small"
      border
      resizable
      highlight-hover-row
      :loading="docLoading"
      :data="docList"
      :columns="docColumns"
      :height="195"
    >
      <template v-slot:uploader="{ row }">
        <div class="uploader-cell">
          <div>{{ row.uploadByName || '--' }}</div>
          <div class="text-muted small">{{ row.uploadTime || '--' }}</div>
        </div>
      </template>
      <template v-slot:docOpt="{ row }">
        <div class="doc-opt-icons">
          <i
            class="el-icon-delete tool-icon"
            :class="{ 'is-disabled': disabled }"
            @click="!disabled && delDoc(row)"
          />
          <i class="el-icon-view tool-icon" @click="previewFile(row.fileUrl)" />
          <i class="el-icon-download tool-icon" @click="downloadFile(row)" />
        </div>
      </template>
    </vxe-grid>
  </div>
</template>

<script>
import EditLoading from '@/mixins/edit-loading'

const FILE_TYPE_REPORT = '3'

export default {
  name: 'InspectionDetailReportDoc',
  mixins: [EditLoading],
  props: {
    inspectionApplyCode: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      docLoading: false,
      docList: [],
      reportUploadTimer: null,
    }
  },
  computed: {
    docColumns() {
      return [
        { type: 'seq', width: 50, align: 'center', title: '#' },
        {
          field: 'fileName',
          title: this.$l('table_label_fileName') || '文件名称',
          minWidth: 220,
          showOverflow: true,
        },
        {
          field: 'uploader',
          title: this.$l('table_label_uploader') || '上传人',
          minWidth: 180,
          slots: { default: 'uploader' },
        },
        {
          field: 'opt',
          title: this.$l('INDEX.table_label_operation') || '操作',
          width: 120,
          align: 'center',
          slots: { default: 'docOpt' },
        },
      ]
    },
  },
  watch: {
    inspectionApplyCode: {
      immediate: true,
      handler(val) {
        if (val) this.loadDocList()
        else this.docList = []
      },
    },
  },
  methods: {
    ensureOk(res, fallbackMsg) {
      if (!res) {
        this.$message.error(fallbackMsg || '接口调用失败')
        return false
      }
      if (res.status !== 200) {
        this.$message.error(res.msg || fallbackMsg || '接口调用失败')
        return false
      }
      return true
    },
    parseListData(res) {
      const body = res && res.body
      if (!body) return []
      return Array.isArray(body.data) ? body.data : []
    },
    async loadDocList() {
      if (!this.inspectionApplyCode) return
      this.docLoading = true
      const res = await this.$api
        .dfInspectionApplyDocListApi({
          inspectionApplyCode: this.inspectionApplyCode,
          fileType: FILE_TYPE_REPORT,
        })
        .catch(() => null)
      this.docLoading = false
      if (
        !this.ensureOk(
          res,
          this.$l('tips_inspection_report_doc_list_fail') || '获取验货报告失败',
        )
      ) {
        this.docList = []
        return
      }
      this.docList = this.parseListData(res)
    },
    triggerReportUpload() {
      if (this.disabled || !this.inspectionApplyCode) return
      this.$refs.reportUploadBtn.$el.click()
    },
    onReportFilesChangeDebounced() {
      clearTimeout(this.reportUploadTimer)
      this.reportUploadTimer = setTimeout(() => {
        const uploader = this.$refs.reportUploader
        const fileList = (uploader && uploader.uploadFiles) || []
        const rawFiles = fileList.map((f) => f.raw).filter(Boolean)
        if (!rawFiles.length) return
        this.processReportFiles(rawFiles)
        uploader.clearFiles()
      }, 300)
    },
    async processReportFiles(rawFiles) {
      if (this.loadingOpen()) return
      const itemList = []
      for (const raw of rawFiles) {
        const res = await this.$api.SYS_UPLOAD(raw).catch(() => null)
        if (!this.ensureOk(res, this.$l('tips_upload_fail') || '上传失败')) continue
        if (!res.body) continue
        itemList.push({
          inspectionApplyCode: this.inspectionApplyCode,
          fileType: FILE_TYPE_REPORT,
          fileCode: res.body.fileId,
          fileName: raw.name,
          fileUrl: res.body.url,
        })
      }
      this.loadingClose()
      if (!itemList.length) return
      const saveRes = await this.$api
        .dfInspectionApplyDocSaveApi({ itemList })
        .catch(() => null)
      if (!saveRes || saveRes.status !== 200) {
        this.$message.error(
          this.$l('tips_inspection_report_doc_save_fail') || '保存失败',
        )
        return
      }
      this.$message.success(
        this.$l('tips_inspection_report_doc_save_ok') || '保存成功',
      )
      this.loadDocList()
    },
    async delDoc(row) {
      const code = row.inspectionApplyDocCode
      if (!code) return
      const ok = await this.$confirm(
        this.$l('tips_inspection_report_doc_del_confirm') ||
          '确定删除该验货报告？',
        this.$l('dialog_title_confirm') || '确认信息',
        {
          type: 'warning',
          confirmButtonText: this.$l('INDEX.btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
        },
      )
        .then(() => true)
        .catch(() => false)
      if (!ok) return
      const res = await this.$api
        .dfInspectionApplyDocDelApi({ listInspectionApplyDocCode: [code] })
        .catch(() => null)
      if (!res || res.status !== 200) {
        this.$message.error(
          this.$l('tips_inspection_report_doc_del_fail') || '删除失败',
        )
        return
      }
      this.$message.success(
        this.$l('tips_inspection_report_doc_del_ok') || '删除成功',
      )
      this.loadDocList()
    },
    previewFile(url) {
      if (url) window.open(url, '_blank')
    },
    downloadFile(row) {
      this.$api.SYS_FILEDOWNLOAD(row.fileCode)
    },
  },
  beforeDestroy() {
    clearTimeout(this.reportUploadTimer)
  },
}
</script>

<style lang="scss" scoped>
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.section-head {
  padding-bottom: 8px;
}
.uploader-cell {
  line-height: 1.4;
  font-size: 12px;
}
.text-muted {
  color: #909399;
}
.small {
  font-size: 12px;
}
.doc-opt-icons {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.tool-icon {
  cursor: pointer;
  color: #909399;
  font-size: 16px;
}
.tool-icon:hover {
  color: #409eff;
}
.tool-icon.is-disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
.d-none {
  display: none !important;
}
</style>
